// mf-buy-engine — the page-wide purchase layer
//
// Owns: variant selection (swatches / selects), the buy drawer, add-to-cart and
// the sticky mobile bar. Every piece of content is rendered by Liquid; this file
// only ever does three things:
//   1. rewrite the text and image src of nodes that already exist, on selection
//   2. move the pre-rendered drawer content into the drawer shell (move, not build)
//   3. post to /cart/add.js
//
// Without this section the CTAs stay real product-page links, so buying still works.

const SEL = {
  scope: '[data-mf-product]',
  swatch: '[data-mf-swatch]',
  select: '[data-mf-select]',
  optGroup: '[data-mf-opt-group]',
};

/** Shopify money formatting. money_format looks like "${{amount}}". */
function formatMoney(cents, format) {
  const value = (cents / 100).toFixed(2);
  const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (!format) return '$' + withCommas;
  return format
    .replace(/\{\{\s*amount\s*\}\}/g, withCommas)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toLocaleString('en-US'))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(cents / 100).toString());
}

/** Candidate widths for a swapped-in variant image.
 *
 *  Capped at 1200 because that is what mf-variant-data renders `m` at, sized for
 *  the widest spot that shows it (the single-product drawer, ~390 CSS px on a 3x
 *  screen). The lower steps matter just as much: the same variant image also
 *  lands in the scene cards, which are 45vw on a phone — about 165 CSS px, so
 *  375w or 640w depending on the device, not 1200w.
 */
const SWAP_WIDTHS = [375, 640, 750, 1000, 1200];

/** Derive a srcset from one variant image url.
 *
 *  Shopify serves any width off the same url via ?width=, so the whole ladder
 *  comes from the single url in the variant data — no extra Liquid, no second
 *  request. Returns '' when the url carries no width to rewrite, and the caller
 *  then falls back to a plain src.
 */
function variantSrcset(url) {
  if (!url || !/[?&]width=\d+/.test(url)) return '';
  return SWAP_WIDTHS
    .map((w) => `${url.replace(/([?&]width=)\d+/, `$1${w}`)} ${w}w`)
    .join(', ');
}

/** Smallest candidate in an <img>'s srcset, or its src when there is none. */
function smallestSource(img) {
  const set = img.getAttribute('srcset') || '';
  let best = null;
  set.split(',').forEach((part) => {
    const [url, d] = part.trim().split(/\s+/);
    const w = parseInt(d || '', 10) || Infinity;
    if (url && (!best || w < best.w)) best = { url, w };
  });
  return best ? best.url : img.src;
}

class MfBuyEngine extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.getAttribute('data-mf-money') || '${{amount}}';
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mqDesktop = window.matchMedia('(min-width: 1024px)');
    // Cached because it is read on every scroll event; invalidated where the
    // value can actually change (resize / load — see setupBuybar)
    this.headerH = null;
    this.variantCache = new Map();
    this.openPanel = null;
    this.openSourceId = null;
    this.cartPrimed = null;      // primed on drawer open, consumed by addToCart
    // True only while the detail panel was opened *with* the drawer (desktop
    // single-product). Those two came up together, so they go away together.
    // Stays false when the customer opened details themselves — e.g. tapping a
    // product name inside a set drawer, where closing details must leave the
    // drawer standing.
    this.detailAuto = false;

    this.drawer = this.querySelector('[data-mf-drawer]');
    this.drawerPanel = this.querySelector('[data-mf-drawer-panel]');
    this.drawerSlot = this.querySelector('[data-mf-drawer-slot]');
    this.scrim = this.querySelector('[data-mf-drawer-scrim]');
    this.buybar = this.querySelector('[data-mf-buybar]');
    this.detailClip = this.querySelector('[data-mf-detail-clip]');
    this.detailSlot = this.querySelector('[data-mf-detail-slot]');
    this.detailPanel = null;
    this.announce = this.querySelector('[data-mf-announce]');

    this.onClick = this.handleClick.bind(this);
    this.onChange = this.handleChange.bind(this);
    this.onKey = this.handleKey.bind(this);
    document.addEventListener('click', this.onClick);
    document.addEventListener('change', this.onChange);
    document.addEventListener('keydown', this.onKey);
    this.onScrollCapture = this.handleDetailScroll.bind(this);
    document.addEventListener('scroll', this.onScrollCapture, true);

    this.dedupeSharedData();
    this.setupBuybar();

    // Re-wire after the theme editor re-renders a section
    this.onSectionLoad = () => {
      this.variantCache.clear();
      this.dedupeSharedData();
      this.setupBuybar();
    };
    document.addEventListener('shopify:section:load', this.onSectionLoad);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick);
    document.removeEventListener('change', this.onChange);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('scroll', this.onScrollCapture, true);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    if (this.buybarStop) this.buybarStop();
    this.unlockScroll();
    // The iframe that holds the unwrapped fetch goes with the component. The
    // bound copy dies with its frame, so drop the reference too — a later
    // rawFetch() then builds a fresh one instead of calling into a dead window.
    if (this._rawFrame) { this._rawFrame.remove(); this._rawFrame = null; }
    this._rawFetch = null;
  }

  // ── Shared product data ─────────────────────────────────────
  /** Drop duplicate variant islands and detail panels, and say something when
   *  one is missing.
   *
   *  Both only ever describe the product itself, and every lookup here takes
   *  the first match by handle, so any further copy is dead weight. Liquid
   *  cannot see across sections, so each one can only de-duplicate within
   *  itself: on SnapPlay that produced 36 copies of 7 products, 28% of the
   *  page. The fix is the buy engine's own "Products used on this page" list
   *  plus "Product data is shared" on each section — this is the safety net
   *  for when only one half of that is configured. It removes nodes the page
   *  already rendered; it never creates any.
   *
   *  The audit is editor-only. A product with no island still renders, still
   *  links to its product page and still adds to cart on its Liquid-rendered
   *  variant — only switching options goes quiet, which is exactly the kind of
   *  failure nobody notices until a customer reports it.
   */
  dedupeSharedData() {
    ['[data-mf-variants]', '[data-mf-detail-panel]'].forEach((sel) => {
      const attr = sel.slice(1, -1);
      const seen = new Set();
      document.querySelectorAll(sel).forEach((node) => {
        const key = node.getAttribute(attr);
        if (!key) return;
        if (seen.has(key)) node.remove();
        else seen.add(key);
      });
    });

    if (!window.Shopify || !window.Shopify.designMode) return;
    // Openers pointing at a drawer that was never rendered — a section ticked
    // "Product data is shared" while its product is missing from the buy
    // engine's list. The card then only links out; the drawer never opens.
    const orphans = new Set();
    document.querySelectorAll('[data-mf-open-cart]').forEach((el) => {
      const id = el.getAttribute('data-mf-open-cart');
      if (id && !document.querySelector(`[data-mf-cart-panel="${CSS.escape(id)}"]`)) orphans.add(id);
    });
    if (orphans.size) {
      console.warn('[MF Buy Engine] No drawer rendered for: ' + [...orphans].join(', ') + '. Add the product to "Products used on this page" or untick "Product data is shared" on that section.');
    }
    const missing = new Set();
    document.querySelectorAll('[data-mf-product]').forEach((el) => {
      const h = el.getAttribute('data-mf-product');
      if (h && !document.querySelector(`[data-mf-variants="${CSS.escape(h)}"]`)) missing.add(h);
    });
    if (missing.size) {
      console.warn(
        '[MF Buy Engine] No variant data for: ' + [...missing].join(', ') +
        '. Colour and option switching will not update prices or images for these. ' +
        'Add them to "Products used on this page" on the MF Buy Engine section, ' +
        'or untick "Product data is shared" on the section that shows them.'
      );
    }
  }

  // ── Variant data ────────────────────────────────────────────
  getVariants(handle) {
    if (this.variantCache.has(handle)) return this.variantCache.get(handle);
    const node = document.querySelector(`[data-mf-variants="${CSS.escape(handle)}"]`);
    let data = [];
    if (node) {
      try { data = JSON.parse(node.textContent); } catch (e) { data = []; }
    }
    this.variantCache.set(handle, data);
    return data;
  }

  /** Read the currently selected option combination inside one product scope */
  readSelection(scope) {
    const picked = [];
    scope.querySelectorAll(SEL.optGroup).forEach((group) => {
      const idx = parseInt(group.getAttribute('data-mf-opt-index'), 10);
      if (Number.isNaN(idx)) return;
      if (group.getAttribute('data-mf-opt-group') === 'color') {
        const on = group.querySelector('[data-mf-swatch][aria-checked="true"]');
        if (on) picked[idx] = on.getAttribute('data-mf-value');
      } else {
        const sel = group.querySelector('select');
        if (sel) picked[idx] = sel.value;
      }
    });
    return picked;
  }

  /** Resolve a variant from the option combination; falls back to the first
   *  match on the dimensions that are actually selected */
  matchVariant(handle, picked) {
    const list = this.getVariants(handle);
    return list.find((v) => v.o.every((val, i) => picked[i] == null || picked[i] === val)) || null;
  }

  /** Mark one swatch as the chosen one within its group. */
  selectSwatch(swatch) {
    const group = swatch.closest(SEL.optGroup);
    if (!group) return;
    group.querySelectorAll(SEL.swatch).forEach((b) => {
      b.setAttribute('aria-checked', 'false');
      const ring = b.querySelector('[data-mf-sw-ring]');
      if (ring) { ring.classList.add('mf-opacity-0'); ring.classList.remove('mf-opacity-100'); }
    });
    swatch.setAttribute('aria-checked', 'true');
    const ring = swatch.querySelector('[data-mf-sw-ring]');
    if (ring) { ring.classList.remove('mf-opacity-0'); ring.classList.add('mf-opacity-100'); }
  }

  /** Carry a card's current choice into the drawer.
   *
   *  The card and the drawer panel are two separate renderings of the same
   *  block, each with its own swatches, so they share no state. Without this the
   *  drawer always opens on the product's default colour and silently discards
   *  the one the reader just picked on the card.
   */
  applySelection(fromScope, toScope) {
    if (!fromScope || !toScope || fromScope === toScope) return;
    const picked = this.readSelection(fromScope);
    if (!picked.length) return;

    picked.forEach((value, idx) => {
      if (value == null) return;
      const group = toScope.querySelector(`[data-mf-opt-group][data-mf-opt-index="${idx}"]`);
      if (!group) return;
      if (group.getAttribute('data-mf-opt-group') === 'color') {
        // Compare attributes rather than building a selector: colour names carry
        // spaces and quotes ("Strike Yellow x Poise Purple").
        const hit = Array.from(group.querySelectorAll(SEL.swatch))
          .find((b) => b.getAttribute('data-mf-value') === value);
        if (hit) this.selectSwatch(hit);
      } else {
        const sel = group.querySelector('select');
        if (sel && sel.value !== value) sel.value = value;
      }
    });

    this.syncScope(toScope);
  }

  /** Seed a drawer with the colour its opener declares.
   *
   *  A colour story's carousel card is a plain link with no swatches of its
   *  own, so applySelection has nothing to carry over and the drawer would
   *  open on the product's default colour — Blackberry on a page about Misty
   *  Cove. The single-product drawer is shared by handle across every story on
   *  the page, so it cannot be rendered in any one story's colour; the card
   *  names the colour instead, through data-mf-preset-color.
   */
  applyPresetColour(fromEl, toScope) {
    const want = fromEl.getAttribute('data-mf-preset-color');
    if (!want) return;
    const group = toScope.querySelector('[data-mf-opt-group="color"]');
    if (!group) return;
    // Compare attributes rather than building a selector: colour names carry
    // spaces and quotes ("Strike Yellow x Poise Purple").
    const hit = Array.from(group.querySelectorAll(SEL.swatch))
      .find((b) => b.getAttribute('data-mf-value') === want);
    // Sold out or renamed: leave the rendered default rather than clearing it.
    if (!hit) return;
    this.selectSwatch(hit);
    this.syncScope(toScope);
  }

  /** Show only the colours purchasable under the dimensions currently
   *  selected, and step off a colour that just became unavailable.
   *
   *  Liquid renders swatches for every colour with stock in any combination;
   *  once the reader picks a device or version the truth narrows to "this
   *  colour with that device". Colours with no stock under the current
   *  selection are hidden outright (not struck through — decided 2026-08-31),
   *  and reappear when a selection that has them returns. When the selected
   *  colour drops out, fall to the first colour still shown (the same rule as
   *  the preset-colour fallback).
   */
  syncSwatchAvailability(scope, handle, picked) {
    const group = scope.querySelector('[data-mf-opt-group="color"]');
    if (!group) return picked;
    const cidx = parseInt(group.getAttribute('data-mf-opt-index'), 10);
    if (Number.isNaN(cidx)) return picked;
    const list = this.getVariants(handle);
    if (!list.length) return picked;

    const availableFor = (value) => list.some((v) =>
      v.a && v.o[cidx] === value &&
      v.o.every((val, j) => j === cidx || picked[j] == null || picked[j] === val));

    let firstOk = null;
    group.querySelectorAll('[data-mf-swatch]').forEach((b) => {
      const ok = availableFor(b.getAttribute('data-mf-value'));
      b.hidden = !ok;
      if (ok && !firstOk) firstOk = b;
    });

    const current = picked[cidx];
    if (current != null && !availableFor(current) && firstOk) {
      this.selectSwatch(firstOk);
      picked[cidx] = firstOk.getAttribute('data-mf-value');
    }
    return picked;
  }

  /** After a selection change, rewrite the text and images already in this block */
  syncScope(scope) {
    const handle = scope.getAttribute('data-mf-product');
    if (!handle) return;
    const picked = this.syncSwatchAvailability(scope, handle, this.readSelection(scope));

    // A scope may expose only some dimensions as controls: the colour-story
    // lines hide the colour swatches entirely because the colour is preset by
    // the section. Fill the missing dimensions from the variant currently on
    // the line, so changing the visible select keeps the preset colour instead
    // of drifting to whichever variant happens to come first. When that exact
    // combination does not exist, retry with the controls alone.
    const currentId = scope.getAttribute('data-mf-variant');
    const current = this.getVariants(handle).find((v) => String(v.i) === currentId);
    const filled = current
      ? current.o.map((val, i) => (picked[i] == null ? val : picked[i]))
      : picked;
    const variant = this.matchVariant(handle, filled) || this.matchVariant(handle, picked);
    if (!variant) return;

    scope.setAttribute('data-mf-variant', variant.i);
    scope.setAttribute('data-mf-price', variant.p);
    scope.setAttribute('data-mf-compare', variant.c);

    // Two hook names for one job, mirroring the colour lookup below: drawer rows
    // use the line-* names, product cards the box-* ones.
    const priceEl = scope.querySelector('[data-mf-line-price], [data-mf-box-price]');
    if (priceEl) priceEl.textContent = formatMoney(variant.p, this.moneyFormat);

    // The colour name must come from the real colour option index: 5 of these 7
    // products list colour second, so picked[0] would print a device or version.
    const colorEl = scope.querySelector('[data-mf-line-color], [data-mf-box-color]');
    if (colorEl) {
      const cg = scope.querySelector('[data-mf-opt-group="color"]');
      const cidx = cg ? parseInt(cg.getAttribute('data-mf-opt-index'), 10) : -1;
      if (cidx >= 0 && picked[cidx] != null) colorEl.textContent = picked[cidx];
    }

    if (variant.m) {
      // A block with its own image opts out: the operator picked that shot on
      // purpose and it must not be replaced by whatever colour is selected.
      const img = scope.querySelector('img:not([data-mf-img-pinned])');
      if (img) this.swapImage(img, variant.m);
    }

    // The detail panel beside the drawer shows the same product: keep its
    // gallery and variant-gated copy on the colour just picked.
    // Only a pick made inside the drawer counts; a card on the page with the
    // same product is not what the reader is looking at.
    if (this.detailPanel && this.drawer && this.drawer.contains(scope) &&
        this.detailPanel.getAttribute('data-mf-detail-panel') === handle) {
      this.syncDetailVariant(this.detailPanel, variant.i);
    }

    const panel = scope.closest('[data-mf-cart-panel]');
    if (panel) this.recalc(panel);
  }

  // ── Image swap ──────────────────────────────────────────────
  /** Cross-fade an <img> to a new source.
   *
   *  Loads the new frame first, so the old image stays on screen instead of the
   *  container flashing its own background — clicking along a row of eleven
   *  swatches, that flicker reads far worse than a hard cut. Once it is in, the
   *  new frame is laid over the old one and the two cross-fade.
   *
   *  Needs a positioned parent to lay the new frame over the old; without one it
   *  falls back to a plain swap rather than dropping a mispositioned layer on the
   *  page. Reduced motion also takes the plain path.
   */
  async swapImage(img, src) {
    if (!img || !src) return;
    const currentSrc = img.currentSrc || img.src;
    if (!currentSrc || currentSrc === src) return;

    // Remember what Liquid asked for before the first swap rewrites it. Without
    // the sizes attribute the browser assumes 100vw and picks two steps too big
    // on a 45vw card.
    if (img.mfSizes == null) img.mfSizes = img.getAttribute('sizes') || '';

    const nextSet = variantSrcset(src);

    const plain = () => {
      // Swapping src alone does nothing while a srcset is present — srcset wins.
      // But dropping srcset outright pins the image to `m`'s own width (1200)
      // for good, which is what used to happen: a 165 CSS px scene card on a
      // phone was left carrying a 1200w file (48KB, against ~20KB at 640w).
      // Rewrite the ladder for the new variant instead and hand the choice back
      // to the browser.
      if (nextSet) {
        img.setAttribute('srcset', nextSet);
        if (img.mfSizes) img.setAttribute('sizes', img.mfSizes);
      } else {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
      }
      img.src = src;
    };

    // mf-image outputs a bare <img>, or wraps it in <picture> when a mobile
    // source is set; the layer has to go in whichever element is positioned.
    let host = img.parentElement;
    if (host && host.tagName === 'PICTURE') host = host.parentElement;
    if (!host || this.reduceMotion || getComputedStyle(host).position === 'static') {
      plain();
      return;
    }

    // Clicking through swatches quickly: the newest swap wins and the earlier
    // ones must not run their tail once they finally decode.
    const token = (img.mfSwapToken || 0) + 1;
    img.mfSwapToken = token;

    const next = new Image();
    // Preload the step the browser will actually settle on. Loading the bare
    // 1200w url here and letting plain() pick a smaller one afterwards would
    // fetch the image twice.
    if (nextSet) {
      next.sizes = img.mfSizes || '';
      next.srcset = nextSet;
    }
    next.src = src;

    // Wait on `load`, not `decode()`. decode() is tied to the rendering pipeline
    // and simply never settles while the tab is not painting — measured here with
    // the image already at complete:true / naturalWidth:800, decode() still
    // pending after 3.4s, which left the image never swapping at all. Same family
    // as requestAnimationFrame pausing in a background tab.
    const ready = next.complete && next.naturalWidth
      ? Promise.resolve(true)
      : new Promise((resolve) => {
        next.addEventListener('load', () => resolve(true), { once: true });
        next.addEventListener('error', () => resolve(false), { once: true });
        // A slow or hung request must not mean the image never changes:
        // fall back to a plain swap rather than waiting forever.
        setTimeout(() => resolve(false), 1200);
      });

    const decoded = await ready;
    if (img.mfSwapToken !== token || !img.isConnected) return;
    if (!decoded) {
      plain();
      return;
    }

    // Drop any layer still mid-flight from a previous swap
    host.querySelectorAll('[data-mf-swap-layer]').forEach((n) => n.remove());

    next.className = img.className;
    // Copy the original's offset box instead of stretching to the host: the
    // carousel card centres a 70% image, which inset:0 would blow up and shift.
    next.style.left = img.offsetLeft + 'px';
    next.style.top = img.offsetTop + 'px';
    next.style.width = img.offsetWidth + 'px';
    next.style.height = img.offsetHeight + 'px';
    next.alt = '';
    next.setAttribute('aria-hidden', 'true');
    next.setAttribute('data-mf-swap-layer', '');
    host.appendChild(next);

    void next.offsetWidth;               // force reflow so the from-state lands first
    next.setAttribute('data-mf-swap-in', '');
    img.setAttribute('data-mf-swap-from', '');

    const finish = () => {
      if (img.mfSwapToken !== token) return;
      plain();
      img.removeAttribute('data-mf-swap-from');
      next.remove();
    };
    next.addEventListener('transitionend', finish, { once: true });
    // transitionend does not fire in a background tab; land the swap regardless
    setTimeout(finish, 700);
  }

  // ── Totals ──────────────────────────────────────────────────
  recalc(panel) {
    // A fixed amount off the set total, not a percentage: the admin's automatic
    // discount is a fixed-amount rule, and a percentage drifts with whatever is
    // in the set (the same 7% is $11.20 on three items but $5.60 on two), which
    // never matches the amount the badge promises. Value is in cents.
    const off = parseInt(panel.getAttribute('data-mf-save-amount') || '0', 10);
    const pickMin = parseInt(panel.getAttribute('data-mf-pick-min') || '0', 10);
    const lines = Array.from(panel.querySelectorAll('[data-mf-line]'));
    const chosen = lines.filter((l) => pickMin === 0 || l.getAttribute('data-mf-picked') === '1');

    // The total is built from the selling price, the basis the cart charges.
    // The compare-at price is for display only: building on it priced any
    // marked-down product above the cart by the size of the markdown.
    let base = 0;
    let list = 0;
    chosen.forEach((l) => {
      const price = parseInt(l.getAttribute('data-mf-price') || '0', 10);
      const compare = parseInt(l.getAttribute('data-mf-compare') || '0', 10);
      base += price;
      list += Math.max(price, compare);
    });

    // No discount below the minimum count. Capped at the total so a discount
    // larger than the basket cannot drive the price negative.
    const effective = pickMin > 0 && chosen.length < pickMin ? 0 : Math.min(off, base);
    const total = base - effective;

    const totalEl = panel.querySelector('[data-mf-cart-total]');
    if (totalEl) totalEl.textContent = formatMoney(total, this.moneyFormat);
    // A set strikes through its undiscounted total, so the gap is exactly the
    // amount on the badge. A single product carries no set discount; it strikes
    // through its own compare-at price, as the Liquid render does.
    const struck = panel.hasAttribute('data-mf-single') ? list : base;
    const compareEl = panel.querySelector('[data-mf-cart-compare]');
    if (compareEl) {
      compareEl.textContent = formatMoney(struck, this.moneyFormat);
      compareEl.hidden = struck <= total;
    }
    const saveEl = panel.querySelector('[data-mf-cart-save]');
    if (saveEl) saveEl.hidden = struck <= total;

    const addBtn = panel.querySelector('[data-mf-cart-add]');
    if (addBtn) addBtn.disabled = chosen.length === 0;

    const hint = panel.querySelector('[data-mf-pick-hint]');
    if (hint && pickMin > 0) {
      const need = pickMin - chosen.length;
      // The wording comes from the same setting as the badge above it, so the two
      // never disagree (a badge reading "Save $10" over a line reading "10% off").
      // Only panels that predate the setting fall back to the bare amount.
      const label = panel.getAttribute('data-mf-save-label')
        || `Save ${formatMoney(off, this.moneyFormat)}`;
      hint.textContent = need > 0
        ? `Add ${need} more to unlock ${label}`
        : `${label} applied`;
    }
  }

  // ── Background scroll ───────────────────────────────────────
  // Held still while an overlay is up, through the theme's own helper — see
  // lockScroll(). Hand-rolled attempts failed here because they all targeted
  // <body>, which is not the scroller on this theme.
  /** Flag on <html> while any of our overlays is on screen.
   *
   *  Third-party widgets sit at z-index 2147483647 (the signed 32-bit maximum),
   *  so nothing of ours can be raised above them — the only way is to push them
   *  down while we need the space. The CSS that does it lives in mf-tailwind.css.
   */
  markOverlay() {
    const on = (this.drawer && !this.drawer.hidden) || this.barOn;
    document.documentElement.toggleAttribute('data-mf-overlay', !!on);
  }

  /** Hold the page still while an overlay is up.
   *
   *  Locks <html>, not <body>. On this theme document.documentElement is the
   *  scroller — body.scrollTop stays 0 no matter how far the page is scrolled —
   *  so every earlier attempt here failed simply by aiming at the wrong element.
   *
   *  The theme's own T4SThemeSP.Helpers.disableBodyScroll is not used: it only
   *  attaches touchmove handlers (nothing at all on desktop), and its second
   *  argument is a CSS selector string rather than an element, so passing a node
   *  silently does nothing. Its touch guard is unnecessary here anyway — the
   *  panels carry overscroll-behavior: contain.
   *
   *  The scroll position is recorded and restored: overflow:hidden should not
   *  move it, but a browser that does clamp it would otherwise dump the reader
   *  back at the top of a very long page.
   */
  lockScroll() {
    if (this.scrollLockedBy) return;
    const root = document.documentElement;
    this.lockedScrollY = window.scrollY;
    this.lockedOverflow = root.style.overflow;
    root.style.overflow = 'hidden';
    this.scrollLockedBy = 'self';
  }

  unlockScroll() {
    if (!this.scrollLockedBy) return;
    const root = document.documentElement;
    root.style.overflow = this.lockedOverflow || '';
    if (typeof this.lockedScrollY === 'number' && window.scrollY !== this.lockedScrollY) {
      window.scrollTo(0, this.lockedScrollY);
    }
    this.scrollLockedBy = null;
  }

  // ── Drawer ──────────────────────────────────────────────────
  /** Returns false when there is nothing to open — the caller then lets the
   *  click through, so a card stays a working link to its product page. */
  open(sectionId, fromEl) {
    const panel = document.querySelector(`[data-mf-cart-panel="${CSS.escape(sectionId)}"]`);
    if (!panel || !this.drawer) return false;
    if (this.openPanel && this.openPanel !== panel) this.restorePanel();

    this.panelHome = panel.parentNode;
    this.panelNext = panel.nextSibling;
    panel.hidden = false;
    panel.classList.add('mf-flex', 'mf-flex-col', 'mf-grow', 'mf-min-h-0');
    this.drawerSlot.appendChild(panel);
    this.openPanel = panel;
    this.openSourceId = sectionId;
    // Warm the cart count now, off the critical path. It does not depend on the
    // click — the basket is already decided by the time this drawer is open —
    // and through the app wrappers one /cart.js round trip costs ~400ms, which
    // would otherwise sit in front of the first add. Same trick the Studio
    // checkout uses (its E16 fix, 2026-09-05).
    this.primeCartCount();

    this.drawer.hidden = false;
    this.markOverlay();
    // The bar and the drawer both claim the bottom of the screen; the bar drops
    // out of the way while the drawer is up.
    this.measureBuybar();
    this.lockScroll();
    void this.drawerPanel.offsetHeight;      // Force reflow so the closed state lands before the transition
    requestAnimationFrame(() => {
      this.scrim.classList.add('mf-opacity-100');
      this.drawerPanel.setAttribute('data-mf-open', '');
    });
    // Seed the drawer with whatever was chosen on the card that opened it
    const fromScope = fromEl && fromEl.closest(SEL.scope);
    const toScope = panel.querySelector(SEL.scope) || (panel.matches(SEL.scope) ? panel : null);
    if (fromScope && toScope) this.applySelection(fromScope, toScope);
    else if (fromEl && toScope) this.applyPresetColour(fromEl, toScope);

    // First paint of the swatch marks under each line's current dimensions:
    // Liquid's marks mean "no stock in any combination", which is looser than
    // "no stock with the selected device/version".
    panel.querySelectorAll(SEL.scope).forEach((sc) => {
      const h = sc.getAttribute('data-mf-product');
      if (h) this.syncSwatchAvailability(sc, h, this.readSelection(sc));
    });

    this.recalc(panel);

    // Desktop single-product drawers open with the detail panel already out.
    // The copy is the reason the drawer gets opened at all, so "More detail"
    // was an extra click sitting in front of it. Phones keep the button:
    // there is no room to show the two side by side.
    // Called before the focus() below on purpose — openDetail() focuses the
    // detail slot, and for an expansion the customer did not ask for, focus
    // belongs on the drawer itself.
    if (this.mqDesktop.matches && panel.hasAttribute('data-mf-single')) {
      const trigger = panel.querySelector('[data-mf-open-detail]');
      if (trigger) {
        this.openDetail(trigger.getAttribute('data-mf-open-detail'), trigger);
        this.detailAuto = true;
      }
    }

    // preventScroll is required: the panel is fixed, and focus would yank the page
    this.drawerPanel.focus({ preventScroll: true });
    return true;
  }

  close() {
    if (!this.drawer || this.drawer.hidden) return;
    this.closeDetail();
    this.scrim.classList.remove('mf-opacity-100');
    this.drawerPanel.removeAttribute('data-mf-open');
    this.unlockScroll();
    const done = () => {
      this.drawer.hidden = true;
      this.restorePanel();
      this.measureBuybar();
      this.markOverlay();
    };
    // Hide only once the animation is over; still fine when reduced-motion cuts it to 1ms
    setTimeout(done, 460);
  }

  restorePanel() {
    if (!this.openPanel) return;
    this.openPanel.hidden = true;
    this.openPanel.classList.remove('mf-flex', 'mf-flex-col', 'mf-grow', 'mf-min-h-0');
    if (this.panelHome) this.panelHome.insertBefore(this.openPanel, this.panelNext);
    this.openPanel = null;
    this.openSourceId = null;
  }

  // ── Product details ─────────────────────────────────────────
  // The panel slides out from the drawer's left edge (desktop) or covers the
  // screen (mobile). Its content is a Liquid-rendered node; this only moves and
  // shows it.
  openDetail(uid, opener) {
    const panel = document.querySelector(`[data-mf-detail-panel="${CSS.escape(uid)}"]`);
    if (!panel || !this.detailClip) return;
    if (this.detailPanel && this.detailPanel !== panel) this.restoreDetail();

    // The panel is rendered once per product, but the reader arrives with a
    // variant already picked — in the drawer row or the card that opened it.
    const vid = this.detailVariantFor(uid, opener);
    if (vid) this.syncDetailVariant(panel, vid);

    // Details live inside the drawer container, which is hidden by default. When a
    // product card is clicked straight from the page the drawer is not open, so the
    // container and scrim have to be revealed (the drawer panel stays closed).
    if (this.drawer.hidden) {
      this.drawer.hidden = false;
      this.detailStandalone = true;
      this.lockScroll();
      requestAnimationFrame(() => this.scrim.classList.add('mf-opacity-100'));
    }

    this.detailHome = panel.parentNode;
    this.detailNext = panel.nextSibling;
    panel.hidden = false;
    panel.classList.add('mf-flex', 'mf-flex-col', 'mf-grow', 'mf-min-h-0', 'mf-relative', 'mf-h-full');
    this.detailSlot.appendChild(panel);
    this.detailPanel = panel;

    // Always start from the top and reset the floating title bar
    const scroll = panel.querySelector('[data-mf-detail-scroll]');
    if (scroll) scroll.scrollTop = 0;
    this.toggleDetailBar(panel, false);

    this.loadRemoteDetail(panel);
    this.detailClip.hidden = false;
    void this.detailSlot.offsetHeight;
    // Reopening: the imported videos were reset when the panel last closed.
    // Only once the clip is shown — Kalles sizes the player from its box, and
    // a hidden one measures 0×0.
    this.initBgVideos(panel);
    requestAnimationFrame(() => {
      this.detailSlot.setAttribute('data-mf-open', '');
    });
    this.detailSlot.focus({ preventScroll: true });
  }

  /** Pull the product page's own content into the detail panel.
   *
   *  Business rule: the reader must be able to understand the product without
   *  leaving the page — this panel replaces a visit to the product page, so it
   *  carries the page's real content rather than a summary:
   *    tabs      the Product Features / Specifications / Shipping / FAQ tab
   *              panels from the main product section, stacked as plain blocks
   *    sections  every marketing section below the main product section
   *              (dev_banner, dev_image_text, slideshows...), imported whole
   *
   *  That content lives in sections configured per product template, not in
   *  product fields, so Liquid cannot reach it — only a request to the product
   *  URL can. Everything imported is server-rendered HTML moved into the panel;
   *  nothing is composed in JS. Scripts inside the import never execute
   *  (DOMParser marks them non-executable) and are stripped for clarity;
   *  Kalles' lazyloadt4s images keep working because lazysizes watches DOM
   *  mutations. Sections gated by data-variant-filter are all imported and
   *  shown only for the variant picked in the drawer (syncDetailVariant),
   *  mirroring what that page shows for it. App blocks (reviews) are left out.
   *
   *  Fetched once per product, only when the panel opens. On failure the panel
   *  keeps what Liquid rendered: gallery, name and metafield blocks.
   */
  async loadRemoteDetail(panel) {
    const tabsSlot = panel.querySelector('[data-mf-detail-remote="tabs"]');
    const secSlot = panel.querySelector('[data-mf-detail-remote="sections"]');
    const url = tabsSlot && tabsSlot.getAttribute('data-mf-remote-url');
    if (!url || panel.dataset.mfRemoteLoaded) return;
    panel.dataset.mfRemoteLoaded = '1';

    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      this.fillDetailTabs(tabsSlot, doc);
      this.fillDetailSections(secSlot, doc, panel.getAttribute('data-mf-detail-variant'));
      this.fillDetailGallery(panel, doc);
      // The full gallery is in now: highlight the picked variant's own shot,
      // which was usually beyond the first eight Liquid rendered.
      const vid = panel.getAttribute('data-mf-detail-variant');
      if (vid) this.syncDetailVariant(panel, vid);
    } catch (e) {
      // Keep what is already there; the panel stays usable without this block
    }
  }

  /** The variant the detail panel should follow: the row or card that opened
   *  it first, then the open drawer's row for the same product. */
  detailVariantFor(handle, opener) {
    const own = opener && opener.closest('[data-mf-product][data-mf-variant]');
    if (own && own.getAttribute('data-mf-product') === handle) return own.getAttribute('data-mf-variant');
    if (this.openPanel) {
      const row = this.openPanel.querySelector(`[data-mf-product="${CSS.escape(handle)}"][data-mf-variant]`);
      if (row) return row.getAttribute('data-mf-variant');
    }
    return null;
  }

  /** Point the detail panel at one variant.
   *
   *  Products split by variant ("变体分离", e.g. Frame Case MD023: the Kaffee
   *  Brown shell has its own photo set and marketing sections, the other
   *  colours share another) need the panel to follow the colour picked in the
   *  drawer. Two things move, both mirroring the product page itself:
   *    gallery   the hero jumps to the variant's featured image, as the
   *              product page's gallery does on a variant change
   *    sections  imported sections gated by data-variant-filter show only
   *              when they list this variant (see fillDetailSections)
   */
  syncDetailVariant(panel, vid) {
    if (!panel || !vid) return;
    vid = String(vid);
    panel.setAttribute('data-mf-detail-variant', vid);

    panel.querySelectorAll('[data-mf-variant-gate]').forEach((sec) => {
      sec.hidden = !sec.getAttribute('data-mf-variant-gate').split(',').includes(vid);
    });

    const handle = panel.getAttribute('data-mf-detail-panel');
    const variant = this.getVariants(handle).find((v) => String(v.i) === vid);
    if (!variant || !variant.m) return;

    const pathOf = (u) => {
      try {
        return new URL(u, window.location.origin).pathname;
      } catch (e) {
        return '';
      }
    };
    const want = pathOf(variant.m);
    const thumb = Array.from(panel.querySelectorAll('[data-mf-detail-thumb]')).find((b) => {
      const im = b.querySelector('img');
      return pathOf(b.getAttribute('data-mf-master') || (im && im.getAttribute('src')) || '') === want;
    });
    if (thumb) {
      this.pickDetailImage(thumb);
      // Scroll the thumbnail track only — scrollIntoView would drag the
      // panel's own scroller along with it
      const track = thumb.parentElement;
      if (track) {
        const tr = track.getBoundingClientRect();
        const br = thumb.getBoundingClientRect();
        track.scrollTop += br.top - tr.top - (tr.height - br.height) / 2;
        track.scrollLeft += br.left - tr.left - (tr.width - br.width) / 2;
      }
      return;
    }
    // Shot not among the thumbnails yet (the rest arrive with the remote
    // fetch): put it in the hero now, the thumbnail catches up after the fill.
    panel.querySelectorAll('[data-mf-detail-thumb]').forEach((t) => {
      t.classList.remove('mf-border-hairline');
      t.classList.add('mf-border-line');
    });
    const big = panel.querySelector('[data-mf-detail-big] img');
    if (big) this.swapImage(big, variant.m);
  }

  /** The product page's tab widget, rebuilt as tabs beside the cover image —
   *  the same switch-to-read pattern the product page uses. Only the chrome is
   *  ours; every panel's content is the page's own server-rendered HTML. */
  fillDetailTabs(slot, doc) {
    if (!slot) return;
    const tabs = [];
    doc.querySelectorAll('.t4s-product-tabs-wrapper .t4s-tab-wrapper').forEach((tab) => {
      const titleEl = tab.querySelector('.t4s-tab__text') || tab.querySelector('.t4s-tab__title');
      const content = tab.querySelector('.t4s-tab-content');
      if (!titleEl || !content) return;
      const body = document.importNode(content, true);
      body.querySelectorAll('script, noscript').forEach((s) => s.remove());
      // The page's tab CSS keeps inactive panels display:none; visibility is
      // ours to manage here
      body.removeAttribute('class');
      body.removeAttribute('style');
      body.setAttribute('data-mf-detail-copy', '');
      // "Product Features" / "Product Specifications" → "Features" / "Specifications":
      // the panel is already about one product, the prefix is noise here
      tabs.push({ title: titleEl.textContent.trim().replace(/^product\s+/i, ''), body });
    });
    if (!tabs.length) return;

    const nav = document.createElement('div');
    nav.setAttribute('role', 'tablist');
    nav.className = 'mf-flex mf-flex-row mf-flex-wrap mf-gap-2';
    const panels = document.createElement('div');

    tabs.forEach(({ title, body }, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('data-mf-tab', String(i));
      btn.setAttribute('aria-selected', String(i === 0));
      btn.textContent = title;
      nav.appendChild(btn);

      body.setAttribute('role', 'tabpanel');
      body.setAttribute('data-mf-tab-panel', String(i));
      body.hidden = i !== 0;
      panels.appendChild(body);
    });

    slot.append(nav, panels);
    slot.hidden = false;
  }

  /** The marketing sections below the main product section, imported whole. */
  /** Top the gallery up from the product page, so it ends up showing the same
   *  shots the PDP does.
   *
   *  Liquid renders only the first 8 so the panel is never empty on open.
   *  Rendering every shot server-side would have cost ~520KB of extra markup
   *  on snapplay alone (823 images across its nine products, measured
   *  2026-09-18, against a 1.2MB page) for images most customers never reach.
   *  This fetch is already being made for the tabs and the long-form copy, so
   *  the rest of the gallery rides along for free.
   *
   *  The product page keeps the unsized CDN url on `data-master`, which is what
   *  makes this cheap: one url per shot, and every size is a query parameter
   *  away. Reading `src` instead would pick up whatever width lazysizes had
   *  settled on.
   */
  fillDetailGallery(panel, doc) {
    const thumbs = panel.querySelector('[data-mf-detail-thumbs]');
    if (!thumbs) return;

    const masters = [];
    doc.querySelectorAll('img[data-master]').forEach((im) => {
      const m = im.getAttribute('data-master');
      if (m && !masters.includes(m)) masters.push(m);
    });
    if (!masters.length) return;

    // Match on the CDN path alone: the same shot is already on the page at a
    // different width, so the query string differs and full urls never match.
    const pathOf = (u) => {
      try {
        return new URL(u, window.location.origin).pathname;
      } catch (e) {
        return '';
      }
    };
    const have = new Set();
    thumbs.querySelectorAll('[data-mf-detail-thumb]').forEach((b) => {
      const im = b.querySelector('img');
      const p = pathOf(b.getAttribute('data-mf-master') || (im && im.getAttribute('src')) || '');
      if (p) have.add(p);
    });

    const frag = document.createDocumentFragment();
    let idx = thumbs.querySelectorAll('[data-mf-detail-thumb]').length;
    masters.forEach((m) => {
      const p = pathOf(m);
      if (!p || have.has(p)) return;
      have.add(p);
      frag.appendChild(this.buildDetailThumb(m, idx));
      idx += 1;
    });
    if (!frag.childNodes.length) return;
    thumbs.appendChild(frag);
  }

  /** One gallery thumbnail, matching what mf-product-detail renders in Liquid. */
  buildDetailThumb(master, index) {
    const sep = master.indexOf('?') === -1 ? '?' : '&';
    const at = (w) => `${master}${sep}width=${w}`;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className =
      'mf-shrink-0 mf-w-[60px] mf-h-[60px] lg:mf-w-[72px] lg:mf-h-[72px] mf-p-0 mf-rounded ' +
      'mf-bg-bg-subtle mf-overflow-hidden mf-cursor-pointer mf-border-ring ' +
      'mf-transition-colors mf-duration-fast mf-border-line';
    btn.setAttribute('data-mf-detail-thumb', String(index));
    btn.setAttribute('data-mf-master', at(1200));
    btn.setAttribute('aria-label', `View image ${index + 1}`);

    const img = document.createElement('img');
    img.className = 'mf-w-full mf-h-full mf-object-cover';
    img.alt = '';
    img.width = 72;
    img.height = 72;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.sizes = '72px';
    img.srcset = `${at(80)} 80w, ${at(160)} 160w, ${at(240)} 240w`;
    img.src = at(160);
    btn.appendChild(img);
    return btn;
  }

  fillDetailSections(slot, doc, pickedVid) {
    if (!slot) return;
    const mainC = doc.getElementById('MainContent');
    const mainInner = mainC && mainC.querySelector('.t4s-section-main-product');
    const mainSec = mainInner && mainInner.closest('.shopify-section');
    if (!mainC || !mainSec) return;

    // Variant-gated sections are all imported and shown for the variant the
    // reader picked — the same rule the product page's variant-section-filter.js
    // applies (comma list, full-width commas too). Without a picked variant,
    // fall back to the product page's own selected one. A hidden section's
    // images stay unloaded: lazysizes skips display:none.
    const vidInput = doc.querySelector('form [name="id"]');
    const vid = pickedVid ? String(pickedVid) : (vidInput ? String(vidInput.value) : '');

    const all = Array.from(mainC.children).filter((el) => el.classList.contains('shopify-section'));
    const after = all.slice(all.indexOf(mainSec) + 1);
    const seenCss = new Set(
      Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map((l) => l.getAttribute('href'))
    );

    let added = 0;
    after.forEach((sec) => {
      // App embeds (reviews widgets) belong to the product page, not this panel
      if (sec.querySelector('.shopify-app-block, .jdgm-widget')) return;
      // Cross-sell blocks (product cards, add-to-cart forms for other products)
      // stay out: this panel is about one product, and the drawer already owns
      // selling. Their links would also navigate the reader away.
      if (sec.querySelector('.t4s-product, form[action*="/cart/add"]')) return;
      // mf- sections dropped into the product template (an MF Buy Engine on
      // MD030's) stay out too. Imported, the engine would upgrade into a
      // second live instance inside this hidden panel, with its own copies of
      // the detail panels: from then on it grabs every "More detail" click and
      // moves the panel into its own invisible drawer, so details open blank.
      if (sec.querySelector('mf-buy-engine, [data-mf-cart-panel], [data-mf-detail-panel]')) return;
      const gate = sec.querySelector('[data-variant-filter]');
      const allowed = gate
        ? (gate.getAttribute('data-variant-filter') || '')
          .replace(/，/g, ',').split(',').map((s) => s.trim()).filter(Boolean)
        : [];

      const node = document.importNode(sec, true);
      if (allowed.length) {
        // The product page ships the non-matching ones with [hidden]; the
        // gate on the section root is ours to drive from here on
        node.querySelectorAll('[data-variant-filter]').forEach((el) => {
          el.hidden = false;
          el.removeAttribute('data-variant-filter');
        });
        node.setAttribute('data-mf-variant-gate', allowed.join(','));
        node.hidden = !!vid && !allowed.includes(vid);
      }
      node.querySelectorAll('script, noscript').forEach((s) => s.remove());
      // The same stylesheet is linked from inside many sections; keep one of each
      node.querySelectorAll('link[rel="stylesheet"]').forEach((l) => {
        const href = l.getAttribute('href');
        if (seenCss.has(href)) l.remove();
        else seenCss.add(href);
      });
      // Belt and braces: should lazysizes ever be absent, promote the deferred
      // sources so the images still load
      if (!window.lazySizes) {
        node.querySelectorAll('img[data-src]').forEach((img) => { img.src = img.getAttribute('data-src'); });
        node.querySelectorAll('img[data-srcset], source[data-srcset]').forEach((el) => {
          el.setAttribute('srcset', el.getAttribute('data-srcset'));
        });
      }
      slot.appendChild(node);
      added += 1;
    });
    if (added) slot.hidden = false;
    this.initBgVideos(slot);
  }

  /** Kalles background videos (MD030's product page has one) are empty
   *  placeholders until the theme's own script inserts the player, which it
   *  only does on page load — imported, they stay a grey block. Run that same
   *  initializer for them; it skips any already loaded. */
  initBgVideos(root) {
    if (!root.querySelector('[data-video-background]:not([loaded="true"])')) return;
    if (!window.T4SThemeSP || typeof window.T4SThemeSP.BgVideo !== 'function') return;
    try {
      window.T4SThemeSP.BgVideo();
    } catch (e) {
      // Leave the placeholder; the rest of the panel is unaffected
    }
  }

  /** Moving the panel out of the drawer reloads the player iframe without
   *  autoplay, and Kalles keeps a paused player covered: the next open would
   *  be grey again. Put the placeholder back so initBgVideos starts afresh. */
  resetBgVideos(root) {
    root.querySelectorAll('[data-video-background][loaded="true"]').forEach((v) => {
      const frame = v.querySelector('iframe');
      if (!frame) return;
      const slot = document.createElement('div');
      slot.setAttribute('data-bgvideo-insert', '');
      frame.replaceWith(slot);
      v.removeAttribute('loaded');
      v.classList.remove('t4s-bgvideo-playing');
    });
  }

  closeDetail() {
    if (!this.detailClip || this.detailClip.hidden) {
      this.detailAuto = false;
      return false;
    }
    this.detailAuto = false;
    this.detailSlot.removeAttribute('data-mf-open');
    setTimeout(() => {
      this.detailClip.hidden = true;
      this.restoreDetail();
      // Opened standalone (not via the drawer): close the container too, or the
      // scrim keeps covering the page
      if (this.detailStandalone) {
        this.detailStandalone = false;
        this.scrim.classList.remove('mf-opacity-100');
        this.drawer.hidden = true;
        this.unlockScroll();
      }
    }, 540);
    return true;
  }

  restoreDetail() {
    if (!this.detailPanel) return;
    this.detailPanel.hidden = true;
    this.resetBgVideos(this.detailPanel);
    this.detailPanel.classList.remove('mf-flex', 'mf-flex-col', 'mf-grow', 'mf-min-h-0', 'mf-relative', 'mf-h-full');
    if (this.detailHome) this.detailHome.insertBefore(this.detailPanel, this.detailNext);
    this.detailPanel = null;
  }

  /** Once the cover scrolls past, float a thumbnail + product name bar at the top.
   *
   *  Scroll-reveal is desktop behaviour only. On mobile the bar carries the
   *  panel's sole Back button, so it stays up permanently — hidden, there is no
   *  way to leave the panel before the first scroll. Until the cover scrolls
   *  past it is a lone back arrow: data-mf-bar-full gates the full form
   *  (white surface, thumbnail, product name) in CSS. */
  toggleDetailBar(panel, on) {
    const bar = panel.querySelector('[data-mf-detail-bar]');
    if (!bar) return;
    const show = on || !this.mqDesktop.matches;
    bar.classList.toggle('mf-opacity-0', !show);
    bar.classList.toggle('mf--translate-y-2', !show);
    bar.classList.toggle('mf-pointer-events-none', !show);
    bar.toggleAttribute('data-mf-bar-full', on);
  }

  /** Thumbnail to hero image: only rewrites the existing img src, never adds nodes */
  pickDetailImage(thumb) {
    const panel = thumb.closest('[data-mf-detail-panel]');
    if (!panel) return;
    panel.querySelectorAll('[data-mf-detail-thumb]').forEach((t) => {
      t.classList.remove('mf-border-hairline');
      t.classList.add('mf-border-line');
    });
    thumb.classList.add('mf-border-hairline');
    thumb.classList.remove('mf-border-line');

    const src = thumb.querySelector('img');
    const bigWrap = panel.querySelector('[data-mf-detail-scroll] .mf-aspect-square');
    const big = bigWrap ? bigWrap.querySelector('img') : null;
    if (!big) return;
    // Prefer the full-size url: the thumbnail itself is served at 160px, and
    // promoting that into the main frame puts a blurry shot on screen.
    const master = thumb.getAttribute('data-mf-master');
    if (master) this.swapImage(big, master);
    else if (src) this.swapImage(big, src.currentSrc || src.src);
  }

  // ── Add to cart ─────────────────────────────────────────────
  async addToCart(panel, btn) {
    const pickMin = parseInt(panel.getAttribute('data-mf-pick-min') || '0', 10);
    const lines = Array.from(panel.querySelectorAll('[data-mf-line]'))
      .filter((l) => pickMin === 0 || l.getAttribute('data-mf-picked') === '1');
    const items = lines
      .map((l) => ({ id: parseInt(l.getAttribute('data-mf-variant'), 10), quantity: 1 }))
      .filter((i) => i.id);
    if (!items.length) return;

    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    // Writes go through an UNWRAPPED fetch, and go as one batched call.
    //
    // window.fetch on this storefront is wrapped by the installed gift app.
    // Measured 2026-09-11 on the same request, back to back: ~3.6s through the
    // wrapper against ~0.27s unwrapped, because it runs its rule set inline on
    // every add. It also drops calls — a second add moments later resolved with
    // undefined in 1ms, never reaching the network at all. Three lines through
    // that wrapper is upwards of ten seconds, and some of them silently lost.
    //
    // Shopify itself has always accepted {items:[…]}; it was only the wrapper
    // that could not carry it. Out of the wrapper, one request does the set.
    //
    // The cart is still the source of truth, not the response: the timeout and
    // the /cart.js count below stay exactly as they were. An unwrapped fetch
    // removes one source of lies, not the need to verify.
    const before = this.cartPrimed !== null ? this.cartPrimed : await this.cartCount();
    const raw = this.rawFetch();

    const post = (body) => Promise.race([
      raw('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).catch(() => null),
      new Promise((resolve) => setTimeout(() => resolve(null), 6000)),
    ]);

    // /cart/add.js answers the batch form with {items:[…]} — the lines it made.
    let acknowledged = 0;
    const batch = await post({ items });
    if (batch && batch.ok) {
      const j = await batch.json().catch(() => null);
      if (j && Array.isArray(j.items)) acknowledged = j.items.length;
    }

    // Batch refused or lost: one request per line, same unwrapped fetch. A
    // single bad line (sold out, at its cap) fails the whole batch, so this is
    // also how the rest of the set still gets in.
    if (acknowledged <= 0) {
      for (const item of items) {
        const res = await post({ id: item.id, quantity: item.quantity });
        if (res && res.ok) {
          const j = await res.json().catch(() => null);
          if (j && (j.id || j.variant_id || (j.items && j.items.length))) acknowledged += 1;
        }
      }
    }

    // Every line accounted for: the adds are proven and a second /cart.js buys
    // nothing but another ~450ms in front of the drawer. Anything less and we
    // count the cart, letting either signal win — a response can be lost on a
    // line that landed, and a cached read can show a flat delta.
    let added = acknowledged;
    if (acknowledged < items.length) {
      const after = await this.cartCount();
      const delta = before == null || after == null ? 0 : after - before;
      added = Math.max(acknowledged, delta);
    }
    this.cartPrimed = null;      // the primed value is stale the moment we write

    // The gift app never saw those writes. Give it a read it CAN see, so a rule
    // set that watches the cart has its chance to re-run — the trust bar
    // promises a charm over $99 and that promise is now outside our write path.
    // Not awaited and failure is ignored: nothing here depends on it.
    if (added > 0) {
      try { window.fetch(`/cart.js?_=${Date.now()}`, { cache: 'no-store' }).catch(() => {}); }
      catch (e) { /* a wrapper that throws on call is exactly why we stopped using it */ }
    }

    if (added <= 0) {
      btn.textContent = 'Could not add — try again';
      if (this.announce) this.announce.textContent = 'Could not add to cart';
      setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2200);
      return;
    }

    btn.textContent = 'Added to cart';
    if (this.announce) {
      this.announce.textContent = added >= items.length
        ? `Added ${added} item${added > 1 ? 's' : ''} to your cart`
        : `Added ${added} of ${items.length} items to your cart`;
    }
    document.dispatchEvent(new CustomEvent('mf:cart:added', { detail: { items, added } }));

    // Hand over to the theme's own cart drawer: this one has done its job
    this.close();
    this.handOffToThemeCart();
    setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1600);
  }

  /** A copy of fetch the installed apps have not wrapped.
   *
   *  A same-origin iframe gets a pristine window, and its fetch has none of the
   *  storefront's wrappers on it. The frame has to STAY in the document —
   *  removing it invalidates its contentWindow, and the copy dies with it.
   *
   *  Bound to the outer window on purpose: requests then carry this page's
   *  cookies and referrer, which is what the cart endpoints key off.
   *
   *  If anything about this fails (no iframe allowed, no contentWindow), the
   *  wrapped window.fetch is returned instead — slow and occasionally lossy,
   *  but the add path still works, and the verification below catches the rest.
   */
  rawFetch() {
    if (this._rawFetch) return this._rawFetch;
    let pristine = null;
    try {
      const frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      frame.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
      document.body.appendChild(frame);
      const cw = frame.contentWindow;
      if (cw && typeof cw.fetch === 'function') {
        pristine = cw.fetch.bind(window);
        this._rawFrame = frame;
      } else {
        frame.remove();
      }
    } catch (e) {
      pristine = null;
    }
    this._rawFetch = pristine || window.fetch.bind(window);
    return this._rawFetch;
  }

  /** Fetch the cart count in the background and remember it for the next add.
   *  Failure is not an error here: a null simply means the add path takes a
   *  live count, exactly as it did before. */
  primeCartCount() {
    this.cartPrimed = null;
    this.cartCount().then((n) => { this.cartPrimed = n; }, () => { this.cartPrimed = null; });
  }

  /** Current number of items in the cart, or null if it cannot be read.
   *  GET /cart.js passes through the app wrapper unharmed; only writes are
   *  affected, which is why this is a usable yardstick. */
  async cartCount() {
    try {
      const res = await Promise.race([
        // no-store plus a cache buster: a cached read makes the before/after
        // comparison meaningless, and a flat delta reads as failure
        fetch(`/cart.js?_=${Date.now()}`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        }),
        new Promise((resolve) => setTimeout(() => resolve(null), 3000)),
      ]);
      if (!res || !res.ok) return null;
      const data = await res.json();
      return typeof data.item_count === 'number' ? data.item_count : null;
    } catch (e) {
      return null;
    }
  }

  /** Close this drawer and open the theme's cart drawer on top of the new line.
   *
   *  Two steps, deliberately separate:
   *
   *  1. `cart:refresh` makes the legacy theme re-fetch the mini cart so it shows
   *     what was just added. NOT `cart:refresh:opend` — that event's open branch
   *     is gated on the shop's "after add to cart" setting, and when that setting
   *     is anything other than "open the drawer" it navigates the page away to
   *     the cart or straight to checkout. Refresh must never do that.
   *
   *  2. Open the drawer ourselves. Preferred path is the theme's own Drawer API;
   *     if that is missing, click the cart button in the header, which is the
   *     route a shopper takes anyway. If neither exists (cart drawer turned off
   *     in theme settings) nothing happens, and the shopper stays where they are
   *     with the cart count updated — better than an unasked-for navigation.
   */
  handOffToThemeCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    // The drawer slides shut first; opening on the next tick keeps the two
    // panels from overlapping mid-flight.
    setTimeout(() => {
      const theme = window.T4SThemeSP;
      const jq = window.jQuery;
      if (theme && theme.Drawer && typeof theme.Drawer.opend === 'function' && jq) {
        const panel = jq('#t4s-mini_cart');
        if (panel.length) {
          // The mini cart is tabbed; the theme's own post-add flow selects the
          // cart tab first, or the drawer can open on whatever tab was last used.
          if (!theme.cartTabActive) jq('[data-is-tab-cart]').trigger('click');
          theme.Drawer.opend(panel);
          return;
        }
      }
      const trigger = document.querySelector('[data-drawer-options*="t4s-mini_cart"]');
      if (trigger) trigger.click();
    }, 260);
  }

  // ── Sticky mobile bar ───────────────────────────────────────
  setupBuybar() {
    if (!this.buybar) return;
    if (this.buybarStop) this.buybarStop();

    this.buyZones = Array.from(document.querySelectorAll('[data-mf-buy]')).map((el) => ({
      el,
      key: el.getAttribute('data-mf-buy'),
      // The gate is this group's cover: the scene's full-screen image, or the
      // colour story's own photo. The bar is released only once that cover has
      // reached the header, i.e. while it fills the screen — that is the moment
      // the reader knows which group the CTA is talking about.
      gate: el.querySelector('[data-mf-buy-gate]'),
    })).filter((z) => z.key);

    if (!this.buyZones.length) return;

    this.footerEl = document.querySelector('#t4s-footer, footer');
    this.barOn = false;
    this.barKey = '';

    // Measured straight from the scroll event, deliberately not through
    // requestAnimationFrame. Browsers already fire scroll at most once a frame, so
    // rAF buys nothing here — and it pauses whenever the tab is not painting,
    // which would leave the bar stuck out of sight. Whether a control is on screen
    // must not depend on the animation clock.
    const onScroll = () => this.measureBuybar();
    // The header height only moves when the window does; drop the cache there.
    const onRemeasure = () => { this.headerH = null; this.measureBuybar(); };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onRemeasure);
    // A restored scroll position fires no scroll event of its own
    window.addEventListener('load', onRemeasure);
    onRemeasure();

    this.buybarStop = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onRemeasure);
      window.removeEventListener('load', onRemeasure);
    };
  }

  /** Decide whether the bar is up, and which group it is showing. */
  measureBuybar() {
    if (!this.buybar || !this.buyZones) return;
    // The bar is a mobile-only affordance; on desktop every section carries its
    // own CTA in the sticky left column.
    if (this.mqDesktop.matches) {
      if (this.barOn) this.hideBuybar();
      return;
    }

    const vh = window.innerHeight;
    if (this.headerH == null) {
      this.headerH = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--mf-header-h')
      ) || 0;
    }
    const headerH = this.headerH;

    let key = '';
    let gateOk = false;
    for (const z of this.buyZones) {
      const r = z.el.getBoundingClientRect();
      // Sections sit back to back, so at most one can straddle the midline
      if (r.top > vh * 0.5 || r.bottom <= vh * 0.5) continue;
      key = z.key;
      gateOk = z.gate ? z.gate.getBoundingClientRect().top <= headerH : true;
      break;
    }

    const footTop = this.footerEl
      ? this.footerEl.getBoundingClientRect().top
      : Number.POSITIVE_INFINITY;

    // Scrolled back above the first group, or down into the footer, or a drawer
    // is open: the bar goes away. Without the first case it would sit there over
    // the opening screen with nothing to refer to.
    const on = !!key && gateOk && footTop > vh * 0.8 && this.drawer.hidden;

    if (on !== this.barOn) {
      this.barOn = on;
      if (on) {
        this.showBuybar(key, 190);
      } else {
        this.hideBuybar();
      }
      return;
    }

    if (on && key !== this.barKey) this.showBuybar(key);
  }

  /** Raise the bar and flip its contents to `key`.
   *
   *  `delay` separates two cases: the bar has just risen (190ms, so the content
   *  starts after the slide-up), or the bar never moved and only its contents
   *  change (24ms).
   */
  showBuybar(sectionId, delay) {
    const panel = document.querySelector(`[data-mf-cart-panel="${CSS.escape(sectionId)}"]`);
    const layer = this.buybar && this.buybar.querySelector('[data-mf-buybar-layer]');
    if (!panel || !layer) return;

    const prevKey = this.barKey;
    this.buybarTarget = sectionId;
    this.barKey = sectionId;

    this.buybar.hidden = false;
    void this.buybar.offsetHeight;
    this.buybar.classList.remove('mf-translate-y-full');
    this.markOverlay();

    if (this.reduceMotion) {
      this.fillBuybar(layer, panel);
      layer.setAttribute('data-mf-in', '');
      return;
    }

    // The layer that is leaving is a snapshot of what is on screen right now.
    // Cloning beats re-rendering it: whatever the reader was looking at is
    // exactly what slides away.
    if (prevKey && prevKey !== sectionId && layer.hasAttribute('data-mf-in')) {
      const out = layer.cloneNode(true);
      out.removeAttribute('data-mf-in');
      out.setAttribute('data-mf-out', '');
      out.setAttribute('aria-hidden', 'true');
      // The clone carries the inline enter transitions; clear them or it fights
      // the leaving transition declared in CSS.
      out.querySelectorAll('[style]').forEach((n) => { n.style.transition = 'none'; });
      layer.parentElement.insertBefore(out, layer);
      clearTimeout(this.barClear);
      this.barClear = setTimeout(() => out.remove(), (delay || 24) + 780);
    }

    // Put the new content in its start position with no transition at all —
    // otherwise the reused nodes visibly drop down before coming back up.
    layer.removeAttribute('data-mf-in');
    layer.querySelectorAll('[data-mf-buybar-thumb], [data-mf-buybar-char], [data-mf-buybar-money]')
      .forEach((n) => { n.style.transition = 'none'; });

    this.fillBuybar(layer, panel);

    void layer.offsetHeight;

    clearTimeout(this.barIn);
    this.barIn = setTimeout(() => {
      const ease = 'cubic-bezier(.22,.61,.36,1)';
      const tr = (d) => `transform .52s ${ease} ${d}ms, opacity .34s ease ${d}ms`;

      // Thumbnails drop into the stack one after another, like cards being
      // dealt — it also says "this is a set" without a word of copy.
      layer.querySelectorAll('[data-mf-buybar-thumb]').forEach((n, i) => {
        n.style.transition = tr(i * 65);
      });
      layer.querySelectorAll('[data-mf-buybar-char]').forEach((n, i) => {
        n.style.transition = tr(60 + i * 12);
      });
      const money = layer.querySelector('[data-mf-buybar-money]');
      if (money) money.style.transition = tr(150);

      layer.setAttribute('data-mf-in', '');
    }, delay || 24);
  }

  /** Write one group's thumbnails, label and price into a layer. */
  fillBuybar(layer, panel) {
    const thumbs = layer.querySelector('[data-mf-buybar-thumbs]');
    const label = layer.querySelector('[data-mf-buybar-label]');
    const price = layer.querySelector('[data-mf-buybar-price]');
    const compare = layer.querySelector('[data-mf-buybar-compare]');

    if (thumbs) {
      const imgs = Array.from(panel.querySelectorAll('[data-mf-line] img')).slice(0, 3);
      thumbs.textContent = '';
      imgs.forEach((img, i) => {
        const box = document.createElement('span');
        box.className = 'mf-w-[34px] mf-h-[34px] mf-rounded mf-bg-bg-subtle mf-overflow-hidden mf-block mf-shrink-0 mf-ring-2 mf-ring-white';
        box.setAttribute('data-mf-buybar-thumb', '');
        if (i > 0) box.style.marginLeft = '-13px';
        const clone = document.createElement('img');
        // The line image sits in a hidden panel and has usually never loaded, so
        // currentSrc is empty and .src is mf-image's 1024px fallback — for a 34px
        // thumb, three times per group change. Take the smallest srcset step instead.
        clone.src = img.currentSrc || smallestSource(img);
        clone.alt = '';
        clone.loading = 'lazy';
        clone.className = 'mf-w-full mf-h-full mf-object-contain';
        box.appendChild(clone);
        thumbs.appendChild(box);
      });
    }

    if (label) {
      // The panel heading is the scene headline, often a full sentence that the
      // bar cannot fit. A section may supply a short set name instead; fall back
      // to the heading when it does not.
      const short = panel.getAttribute('data-mf-bar-label');
      const title = panel.querySelector('h2');
      const text = (short || (title ? title.textContent : '') || '').trim();
      label.textContent = '';
      // One span per character so the label can unfurl letter by letter.
      // aria reads the whole label off the button, so the split is invisible
      // to assistive tech.
      Array.from(text).forEach((ch) => {
        const span = document.createElement('span');
        span.setAttribute('data-mf-buybar-char', '');
        span.textContent = ch === ' ' ? '\u00a0' : ch;
        label.appendChild(span);
      });
      const open = this.buybar.querySelector('[data-mf-buybar-open]');
      if (open) open.setAttribute('aria-label', text);
    }

    const total = panel.querySelector('[data-mf-cart-total]');
    if (price && total) price.textContent = total.textContent.trim();
    const cmp = panel.querySelector('[data-mf-cart-compare]');
    if (compare) compare.textContent = cmp && !cmp.hidden ? cmp.textContent.trim() : '';
  }

  hideBuybar() {
    if (!this.buybar) return;
    this.barOn = false;
    this.barKey = '';
    this.buybar.classList.add('mf-translate-y-full');
    const layer = this.buybar.querySelector('[data-mf-buybar-layer]');
    if (layer) layer.removeAttribute('data-mf-in');
    this.markOverlay();
  }

  // ── Events ──────────────────────────────────────────────────
  handleClick(e) {
    const swatch = e.target.closest(SEL.swatch);
    if (swatch) {
      e.preventDefault();
      this.selectSwatch(swatch);
      const scope = swatch.closest(SEL.scope);
      if (scope) this.syncScope(scope);
      return;
    }

    const pick = e.target.closest('[data-mf-pick]');
    if (pick) {
      e.preventDefault();
      const line = pick.closest('[data-mf-line]');
      const on = line.getAttribute('data-mf-picked') === '1';
      line.setAttribute('data-mf-picked', on ? '0' : '1');
      pick.setAttribute('aria-checked', String(!on));
      pick.classList.toggle('mf-bg-btn', !on);
      pick.classList.toggle('mf-bg-line', on);
      const svg = pick.querySelector('svg');
      if (svg) svg.style.opacity = on ? '0' : '1';
      line.style.opacity = on ? '0.4' : '1';
      const panel = line.closest('[data-mf-cart-panel]');
      if (panel) this.recalc(panel);
      return;
    }

    const tabBtn = e.target.closest('[data-mf-tab]');
    if (tabBtn) {
      e.preventDefault();
      const slot = tabBtn.closest('[data-mf-detail-remote="tabs"]');
      if (slot) {
        const i = tabBtn.getAttribute('data-mf-tab');
        slot.querySelectorAll('[data-mf-tab]').forEach((b) => b.setAttribute('aria-selected', String(b === tabBtn)));
        slot.querySelectorAll('[data-mf-tab-panel]').forEach((pl) => {
          pl.hidden = pl.getAttribute('data-mf-tab-panel') !== i;
        });
      }
      return;
    }

    const detailOpener = e.target.closest('[data-mf-open-detail]');
    if (detailOpener) {
      // href still points at the product page so it works without JS; with JS the
      // panel opens in place
      e.preventDefault();
      this.openDetail(detailOpener.getAttribute('data-mf-open-detail'), detailOpener);
      return;
    }

    if (e.target.closest('[data-mf-detail-close]')) {
      e.preventDefault();
      // Opened together with the drawer, so it closes together with it —
      // leaving a lone drawer behind would look like the close did half a job.
      if (this.detailAuto) this.close();
      else this.closeDetail();
      return;
    }

    const thumb = e.target.closest('[data-mf-detail-thumb]');
    if (thumb) {
      e.preventDefault();
      this.pickDetailImage(thumb);
      return;
    }

    const opener = e.target.closest('[data-mf-open-cart]');
    if (opener) {
      // No panel for this id (a section marked "shared" whose product is not in
      // the buy engine's list): the card is a real link, let it navigate.
      if (this.open(opener.getAttribute('data-mf-open-cart'), opener)) e.preventDefault();
      return;
    }

    if (e.target.closest('[data-mf-buybar-open], [data-mf-buybar-cta]')) {
      e.preventDefault();
      if (this.buybarTarget) this.open(this.buybarTarget);
      return;
    }

    if (e.target.closest('[data-mf-cart-close]') || e.target === this.scrim) {
      e.preventDefault();
      // Details sit above the drawer, so the scrim closes that layer first
      if (this.closeDetail()) return;
      this.close();
      return;
    }

    const add = e.target.closest('[data-mf-cart-add]');
    if (add) {
      e.preventDefault();
      const panel = add.closest('[data-mf-cart-panel]');
      if (panel) this.addToCart(panel, add);
    }
  }

  handleDetailScroll(e) {
    const box = e.target;
    if (!box || !box.hasAttribute || !box.hasAttribute('data-mf-detail-scroll')) return;
    const panel = box.closest('[data-mf-detail-panel]');
    if (!panel) return;
    const on = box.scrollTop > 320;
    if (panel.__barOn === on) return;   // Write only when the threshold is crossed, not every frame
    panel.__barOn = on;
    this.toggleDetailBar(panel, on);
  }

  handleChange(e) {
    if (!e.target.matches(SEL.select)) return;
    const scope = e.target.closest(SEL.scope);
    if (scope) this.syncScope(scope);
  }

  handleKey(e) {
    if (e.key !== 'Escape') return;
    // Details sit above the drawer; Esc unwinds one layer at a time — unless the
    // two opened together, in which case one press takes both.
    if (this.detailAuto) {
      this.close();
      return;
    }
    if (this.closeDetail()) return;
    this.close();
  }
}

if (!customElements.get('mf-buy-engine')) {
  customElements.define('mf-buy-engine', MfBuyEngine);
}
